// src/views/admin/prediction/result.jsx
"use client";

import { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { getPredictionById, getSimilarAverage, getSimilarCases } from "services/predict";
import {
  Alert,
  AlertIcon,
  Box,
  Heading,
  Text,
  Spinner,
  HStack,
  VStack,
  Button,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Badge,
  Divider,
  Stack,
  useToast,
  useColorModeValue,
  CircularProgress,
  CircularProgressLabel,
  Collapse,
  Flex,
  Wrap,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Progress,
} from "@chakra-ui/react";
import { ChevronRightIcon, ChevronDownIcon } from "@chakra-ui/icons";
// import confetti from "canvas-confetti";
import { useAuth } from "contexts/AuthContext";

export function Num({ value }) {
  const n = typeof value === "string" ? Number(value.replace(/,/g, "")) : (value ?? 0);
  const safe = Number.isFinite(n) ? Number(n) : 0;
  const rounded = Math.round(safe);
  return <>{rounded.toLocaleString("ko-KR")}</>;
}

function InfoItem({ label, value }) {
  const border = useColorModeValue("gray.100", "gray.700");
  const subtle = useColorModeValue("gray.600", "gray.400");
  const textColor = useColorModeValue("gray.800", "white");
  const infoBg = useColorModeValue("gray.50", "gray.700");

  return (
    <Box p={4} bg={infoBg} borderRadius="lg" border="1px solid" borderColor={border}>
      <Text fontSize="xs" color={subtle} mb={1} fontWeight="500" textTransform="uppercase" letterSpacing="wide">
        {label}
      </Text>
      <Text fontWeight="600" color={textColor} fontSize="sm">
        {value ?? "-"}
      </Text>
    </Box>
  );
}

function AccuracyRingTicker({ label, value, ringColor }) {
  const pct = value == null || Number.isNaN(value) ? 0 : Math.max(0, Math.min(100, value));
  const displayValue = Math.round(pct);
  const textColor = useColorModeValue("gray.800", "white");

  return (
    <Box p={0} borderRadius="lg" textAlign="center">
      <CircularProgress value={pct} size={{ base: "70px", md: "90px" }} thickness="10px" color={ringColor} mx="auto">
        <CircularProgressLabel fontSize={{ base: "sm", md: "lg" }} fontWeight="700">
          {displayValue}%
        </CircularProgressLabel>
      </CircularProgress>
      <Text color={textColor} fontWeight="600" fontSize={{ base: "xs", md: "sm" }} mt={2}>
        {label}
      </Text>
    </Box>
  );
}

const accBadges = [
  { key: "total_cost_accuracy_pct", label: "공사비" },
  { key: "building_cost_accuracy_pct", label: "건축비" },
  { key: "civil_cost_accuracy_pct", label: "토목" },
  { key: "electrical_cost_accuracy_pct", label: "전기" },
  { key: "facility_cost_accuracy_pct", label: "설비" },
  { key: "opex_cost_accuracy_pct", label: "경상비" },
  { key: "etc_cost_accuracy_pct", label: "기타" },
  { key: "common_cost_accuracy_pct", label: "공통" },
];

const fmtDT = (s) => {
  if (!s) return "-";
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return "-";
  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    timeZone: "Asia/Seoul",
  }).format(dt);
};
function accuracyColor(value) {
  if (value == null || isNaN(value)) return "gray";
  if (value >= 90) return "green";
  if (value >= 70) return "blue";
  if (value >= 40) return "orange";
  return "red";
}

const SLOPE_OPTION_LABELS = {
  FLAT: "택지 부지 (FLAT)",
  SLOPE: "경사지 부지 (SLOPE)",
};

const STRUCTURE_FIELD_META = [
  { key: "structure_parking", label: "구조 · 주차장" },
  { key: "structure_ground", label: "구조 · 지상층" },
  { key: "structure_standard", label: "구조 · 기준층" },
];

const formatInputNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return value;
  const maxFractionDigits =
    typeof options.maximumFractionDigits === "number"
      ? options.maximumFractionDigits
      : Number.isInteger(num)
      ? 0
      : 2;
  const formatter = new Intl.NumberFormat("ko-KR", {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: 0,
  });
  return formatter.format(num);
};

const parseNumericInput = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatSlopeLabel = (value) => {
  if (!value) return null;
  return SLOPE_OPTION_LABELS[value] || value;
};

const formatYesNo = (value, { trueLabel = "예", falseLabel = "아니오" } = {}) => {
  if (value === 1 || value === "1") return trueLabel;
  if (value === 0 || value === "0") return falseLabel;
  return null;
};

export default function PredictionResult() {
  const { runId } = useParams();
  const toast = useToast();
  const { user = {} } = useAuth?.() || {};
  const canViewAccuracy = ["admin", "super_admin"].includes(user?.role);

  const [showCostDetails, setShowCostDetails] = useState(false);
  const [showInputSummary, setShowInputSummary] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // const [showGif, setShowGif] = useState(false);
  const [similarAverage, setSimilarAverage] = useState(null);
  const [similarAverageLoading, setSimilarAverageLoading] = useState(false);
  const [similarAverageError, setSimilarAverageError] = useState(null);
  const casesDisclosure = useDisclosure();
  const [similarCases, setSimilarCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesError, setCasesError] = useState(null);
  /*
  // 한번만 발사
  const firedOnceRef = useRef(false);

  // 커스텀 컨페티 레이어 관리 (오버레이 위에 표시)
  const confettiCanvasRef = useRef(null);
  const confettiFnRef = useRef(null);

  const ensureConfettiLayer = () => {
    if (confettiFnRef.current) return;
    const canvas = document.createElement("canvas");
    Object.assign(canvas.style, {
      position: "fixed",
      inset: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: 3000, // 🔝 Portal zIndex(2500)보다 높게
    });
    document.body.appendChild(canvas);
    confettiCanvasRef.current = canvas;
    confettiFnRef.current = confetti.create(canvas, { resize: true, useWorker: true });
  };

  const cleanupConfettiLayer = () => {
    try {
      if (confettiCanvasRef.current?.parentNode) {
        confettiCanvasRef.current.parentNode.removeChild(confettiCanvasRef.current);
      }
    } catch (_) {}
    confettiCanvasRef.current = null;
    confettiFnRef.current = null;
  };

  // 컨페티(경량 버전): 양/시간 줄임
  const fireConfetti = () => {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;
    ensureConfettiLayer();
    const c = confettiFnRef.current || confetti;

    const duration = 800; // 전체 0.8초
    const end = Date.now() + duration;

    // 첫 폭발: 입자 수 축소
    c({
      particleCount: 60, // ↓ 120 -> 60
      spread: 60,
      startVelocity: 35,
      scalar: 0.8,
      gravity: 0.9,
      origin: { y: 0.6 },
    });

    // 짧은 꼬리: 입자 수 축소
    (function frame() {
      c({
        particleCount: 18, // ↓ 40 -> 18
        startVelocity: 40,
        spread: 55,
        ticks: 60,
        scalar: 0.75,
        gravity: 0.95,
        origin: { x: Math.random() * 0.5 + 0.25, y: 0.6 },
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };
  */

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await getPredictionById(runId); // { run, prediction, prediction_ui }
        if (mounted) setData(res);
      } catch (e) {
        toast({
          status: "error",
          title: "결과 불러오기 실패",
          description: String(e?.message || e),
          isClosable: true,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      // cleanupConfettiLayer(); // fireworks disabled
    };
  }, [runId, toast]);

  useEffect(() => {
    const currentRunId = data?.run?.id;
    if (!currentRunId) {
      setSimilarAverage(null);
      return;
    }
    let mounted = true;
    (async () => {
      setSimilarAverageLoading(true);
      setSimilarAverageError(null);
      try {
        const payload = await getSimilarAverage(currentRunId);
        if (mounted) {
          setSimilarAverage(payload);
        }
      } catch (err) {
        if (mounted) {
          setSimilarAverageError(String(err?.message || err));
        }
      } finally {
        if (mounted) {
          setSimilarAverageLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [data?.run?.id]);

  const bgGradient = useColorModeValue("linear(to-br, gray.50, blue.50)", "linear(to-br, gray.900, blue.900)");
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.100", "gray.700");
  const cardShadow = useColorModeValue("xl", "dark-lg");
  const chipBg = useColorModeValue("gray.50", "gray.700");
  const boardBg = useColorModeValue("whiteAlpha.700", "whiteAlpha.100");
  const similarSectionBg = useColorModeValue("gray.50", "gray.700");
  const similarBorder = useColorModeValue("gray.200", "gray.600");
  const accent = useColorModeValue("blue.500", "blue.300");
  const textColor = useColorModeValue("gray.800", "gray.100");

  const run = data?.run || null;
  const ui = data?.prediction_ui || run?.result_json || {};

  const createdAt = fmtDT(run?.created_at || run?.requested_at);
  const finishedAt = fmtDT(run?.finished_at);
  const payloadInput = run?.payload_json?.input || run?.payload?.input || {};
  const result = run?.result_json || run?.result || {};

  const basicInfoItems = [
    { label: "유형", value: payloadInput?.유형 },
    { label: "공사명", value: payloadInput?.공사명 },
    { label: "접수일", value: payloadInput?.접수일 },
    { label: "지역", value: payloadInput?.지역 },
    { label: "구", value: payloadInput?.구 },
  ];

  const constructionOverviewItems = [
    { label: "연면적(평)", value: formatInputNumber(payloadInput?.연면적) },
    { label: "마감수준", value: payloadInput?.마감수준 },
    { label: "세대 창호", value: payloadInput?.["세대 창호"] },
    { label: "평균평형", value: formatInputNumber(payloadInput?.평균평형) },
    {
      label: "공기",
      value: formatInputNumber(payloadInput?.공기, { maximumFractionDigits: 1 }),
    },
    { label: "지상층수", value: formatInputNumber(payloadInput?.지상층수) },
    { label: "지하층수", value: formatInputNumber(payloadInput?.지하층수) },
    { label: "난방", value: payloadInput?.난방 },
    { label: "세대수", value: formatInputNumber(payloadInput?.세대수) },
    { label: "공급면적(㎡)", value: formatInputNumber(payloadInput?.["공급면적(㎡)"]) },
    { label: "대지면적(㎡)", value: formatInputNumber(payloadInput?.["대지면적(㎡)"]) },
    { label: "경사 여부", value: formatSlopeLabel(payloadInput?.["경사 여부"]) },
    { label: "동수", value: formatInputNumber(payloadInput?.["동수"]) },
    { label: "해안가 여부", value: formatYesNo(payloadInput?.["해안가 여부"]) },
  ];

  const selectionOptionalFields = [
    { label: "단지외공사", value: payloadInput?.단지외공사 },
    {
      label: "암반비율",
      value: formatInputNumber(payloadInput?.암반비율, { maximumFractionDigits: 1 }),
    },
    { label: "공법", value: payloadInput?.공법 },
  ];

  const structureSelectionItems = STRUCTURE_FIELD_META.map(({ key, label }) => ({
    label,
    value: payloadInput?.[key],
  }));

  const selectionItems = [...selectionOptionalFields, ...structureSelectionItems];

  const costFields = [
    { label: "총 공사비 (백만)", key: "공사비" },
    { label: "건축비 (원/평)", key: "건축비" },
    { label: "토목 (원/평)", key: "토목비" },
    { label: "전기 (원/평)", key: "전기비" },
    { label: "설비 (원/평)", key: "설비비" },
    { label: "경상비 (원/평)", key: "경상비" },
    { label: "기타 (원/평)", key: "기타비" },
    { label: "공통 (원/평)", key: "공통비" },
  ];

  const componentCostFields = costFields.filter((field) => field.key !== "공사비");
  const componentCostSum = componentCostFields.reduce((sum, field) => {
    const num = parseNumericInput(payloadInput?.[field.key]);
    if (num == null) return sum;
    return sum + num;
  }, 0);
  const hasComponentCostInputs = componentCostFields.some((field) =>
    parseNumericInput(payloadInput?.[field.key]) != null
  );
  const formattedPerPyeongInput = hasComponentCostInputs
    ? formatInputNumber(componentCostSum, { maximumFractionDigits: 0 })
    : null;

  const accuracyItems = accBadges
    .map(({ key, label }) => ({ key, label, value: typeof result?.[key] === "number" ? result[key] : null }))
    .filter((x) => x.value !== null);
  const totalCostAccuracyItem = accuracyItems.find((item) => item.key === "total_cost_accuracy_pct");
  const otherAccuracyItems = totalCostAccuracyItem
    ? accuracyItems.filter((item) => item.key !== "total_cost_accuracy_pct")
    : [];
  const ringColorToken = {
    green: "green.400",
    blue: "blue.400",
    orange: "orange.400",
    red: "red.400",
    gray: "gray.400",
  };
  const totalCostAccuracyPct =
    totalCostAccuracyItem?.value == null || Number.isNaN(Number(totalCostAccuracyItem.value))
      ? null
      : Math.round(Math.max(0, Math.min(100, Number(totalCostAccuracyItem.value))));
  const totalCostAccuracyLabel = totalCostAccuracyItem?.label || "공사비";
  const mobileAccuracyColor = accuracyColor(totalCostAccuracyItem?.value);
  const mobileProgressColorScheme = mobileAccuracyColor === "gray" ? "gray" : mobileAccuracyColor;

  const similarMedianTotalCost = similarAverage?.median_total_cost ?? null;
  const similarCount = similarAverage?.count ?? 0;
  const n = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);
  const areaPyeong = n(payloadInput?.연면적);
  const totalMillion = n(ui?.total_cost);  
  const perPyeong = areaPyeong > 0 ? Math.round((totalMillion * 1_000_000) / areaPyeong) : 0;
  const getInputNumber = (obj, keys) => {
    if (!obj) return null;
    for (const key of keys) {
      const value = obj?.[key];
      if (value === undefined || value === null || value === "") continue;
      const num = Number(value);
      if (!Number.isFinite(num)) continue;
      return num;
    }
    return null;
  };
  const inputTotalCost = getInputNumber(payloadInput, ["공사비", "총공사비", "total_cost", "totalCost"]);
  const estimatedSimilarAverage = similarMedianTotalCost
    ? Number(Math.round(similarMedianTotalCost * 10) / 10)
    : null;
  const formatNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString("ko-KR") : value;
  };

  const appliedFilters = similarAverage?.applied_filters || {};
  const payloadRegion = payloadInput?.지역 || payloadInput?.region;
  const typeLabel = appliedFilters?.type || payloadInput?.유형 || payloadInput?.type;
  const locationLabel = appliedFilters?.region || payloadRegion;
  const constructionMethodLabel =
    appliedFilters?.construction_method || payloadInput?.공법 || payloadInput?.construction_method;
  const slopeLabel =
    appliedFilters?.slope_type || payloadInput?.["경사 여부"] || payloadInput?.slope_type;
  const usedRanges = similarAverage?.used_ranges || {};
  const formatRangeLabelFromAPI = (range, unit = "", { digits = 0 } = {}) => {
    if (!range || range.lower == null || range.upper == null) return null;
    const formatter = (value) =>
      Number.isFinite(Number(value))
        ? Number(value).toLocaleString("ko-KR", { maximumFractionDigits: digits })
        : String(value);
    const lowerLabel = formatter(range.lower);
    const upperLabel = formatter(range.upper);
    return `${lowerLabel}${unit} ~ ${upperLabel}${unit}`;
  };
  const similarCriteriaList = [];
  if (typeLabel) similarCriteriaList.push(`유형: ${typeLabel}`);
  if (locationLabel) similarCriteriaList.push(`지역: ${locationLabel}`);
  const areaRangeLabel = formatRangeLabelFromAPI(usedRanges?.area, "평", { digits: 0 });
  if (areaRangeLabel) similarCriteriaList.push(`연면적: ${areaRangeLabel}`);
  const avgRangeLabel = formatRangeLabelFromAPI(usedRanges?.avg, "평", { digits: 0 });
  if (avgRangeLabel) similarCriteriaList.push(`평균평형: ${avgRangeLabel}`);
  const householdsRangeLabel = formatRangeLabelFromAPI(usedRanges?.households);
  if (householdsRangeLabel) similarCriteriaList.push(`세대수: ${householdsRangeLabel}`);
  if (constructionMethodLabel) similarCriteriaList.push(`공법: ${constructionMethodLabel}`);
  if (slopeLabel) similarCriteriaList.push(`경사 여부: ${slopeLabel}`);

  const similarDescription = similarCount
    ? `과거 사례 데이터에서 입력하신 공사 개요와 가장 유사한 ${similarCount.toLocaleString()}건의 실적 데이터를 기준으로 비교한 참고값입니다.`
    : "유사한 사례가 많지 않아 중앙값을 정확히 산출하기 어렵습니다. 현재 예측값을 중심으로 참고하세요.";
  const openCasesModal = async () => {
    if (!run?.id) return;
    casesDisclosure.onOpen();
    setCasesError(null);
    setCasesLoading(true);
    try {
      const payload = await getSimilarCases(run.id, 12);
      setSimilarCases(payload?.cases || []);
    } catch (err) {
      setCasesError(String(err?.message || err));
    } finally {
      setCasesLoading(false);
    }
  };

  const SimilarNotesAlert = ({ heading, description }) => (
    <Alert status="info" borderRadius="md" alignItems="flex-start" mb={2}>
      <AlertIcon mt={0.5} />
      <VStack align="flex-start" spacing={1} fontSize="sm">
        <Text fontWeight="800" color="black.600">
          {heading}
        </Text>
        <Text>
          {description}
        </Text>
      </VStack>
    </Alert>
  );

  const renderSimilarNotesBox = ({ heading, description, onOpenCases }) => (
    <>
      <Box mt={2} p={4} border="1px dashed" borderColor={similarBorder} borderRadius="lg" bg={similarSectionBg}>
        {similarCriteriaList.length > 0 && (
          <VStack align="start" spacing={1} mb={3}>
            <Text fontSize="xs" color="gray.500" fontWeight="600">
              과거 유사 사례 비교 기준
            </Text>
            <Wrap spacing={2}>
              {similarCriteriaList.map((item) => (
                <Badge key={item} colorScheme="gray" variant="solid" px={2} py={1} borderRadius="full" fontSize="10px">
                  {item}
                </Badge>
              ))}
            </Wrap>
          </VStack>
        )}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <Stat>
          <StatLabel>AI 예측 전체공사비 (백만)</StatLabel>
          <StatNumber>
            <Num value={totalMillion} />
          </StatNumber>
          <Text fontSize="xs" color="gray.500">
            AI 예측 값
          </Text>
        </Stat>
        {inputTotalCost !== null && (
          <Stat>
            <StatLabel>입력한 전체공사비 (백만)</StatLabel>
            <StatNumber>
              <Num value={inputTotalCost} />
            </StatNumber>
            <Text fontSize="xs" color="gray.500">
              비용 입력 값
            </Text>
          </Stat>
        )}
        {estimatedSimilarAverage !== null && (
          <Stat>
            <StatLabel>유사 유형 평균 추정 (백만)</StatLabel>
            <StatNumber>
              <Num value={estimatedSimilarAverage} />
            </StatNumber>
            {similarAverage?.count ? (
              <Text fontSize="xs" color="gray.500">
                <Text
                  as="span"
                  color="blue.600"
                  fontWeight="600"
                  textDecoration="underline"
                  cursor="pointer"
                  onClick={onOpenCases}
                >
                  유사 사례 {similarAverage.count.toLocaleString()}건
                </Text>
                {casesLoading && casesDisclosure.isOpen && (
                  <Spinner size="xs" ml={2} />
                )}
                {similarAverage.average_total_cost != null && (
                  <>
                    , 평균{" "}
                    <Text as="span" fontWeight="600">
                      {formatNumber(similarAverage.average_total_cost)}백만
                    </Text>
                  </>
                )}
                {similarAverage.diff_percent != null && (
                  <>
                    , 예측 대비{" "}
                    <Text as="span" fontWeight="600">
                      {Math.abs(similarAverage.diff_percent)}%
                    </Text>{" "}
                    <Text as="span" fontWeight="600">
                      {similarAverage.diff_percent >= 0 ? "높은" : "낮은"}
                    </Text>{" "}
                    수준
                  </>
                )}
                {" "}입니다.
              </Text>
            ) : (
              <Text fontSize="xs" color="gray.500">
                과거 데이터에 확인된 내용이 존재하지 않습니다.
              </Text>
            )}
          </Stat>
        )}
      </SimpleGrid>
      {(similarAverageLoading || similarAverageError) && (
        <Text fontSize="sm" mt={2} color={similarAverageLoading ? "blue.600" : "red.500"}>
          {similarAverageLoading
            ? "유사 사례 평균을 불러오는 중입니다..."
            : similarAverageError}
        </Text>
      )}
      {/* <Text fontSize="sm" color="gray.600">
        • {perPyeong ? `평당 ${perPyeong.toLocaleString()}원 수준으로 ${payloadInput?.유형 || "유사 유형"}에서의 참고치를 제공합니다.` : "평당 정보를 계산할 수 없습니다."}
      </Text> */}
      {/* <Text fontSize="sm" color="gray.600">
        • 오른쪽의 숫자들은 기존 데이터와 비교하여 어떻게 다른지를 빠르게 파악하게 도와주는 참고 시각자료입니다.
      </Text> */}
    </Box>
    </>
  );

  /*
  // 성공 시: 컨페티(경량) + 축하 오버레이 + 레이어 정리
  useEffect(() => {
    if (loading) return;
    if (run?.status !== "succeeded") return;
    if (!firedOnceRef.current) {
      firedOnceRef.current = true;
      fireConfetti();
      setShowGif(true);
      const t = setTimeout(() => {
        setShowGif(false);
        // 컨페티 캔버스도 잠깐 후 제거 (DOM 청결)
        setTimeout(cleanupConfettiLayer, 400);
      }, GIF_DURATION_MS);
      return () => clearTimeout(t);
    }
  }, [loading, run?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  */

  return (
    <>
      {/* {showGif && (
        <Portal>
          <Box
            position="fixed"
            inset="0"
            zIndex={2500}
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
            backdropFilter="blur(4px)"
            bg="rgba(0, 0, 0, 0.08)"
          >
            <VStack
              spacing={6}
              bg="rgba(255, 255, 255, 0.95)"
              backdropFilter="blur(10px)"
              borderRadius="20px"
              p={8}
              boxShadow="0 20px 40px rgba(0, 0, 0, 0.1)"
            >
              <Box fontSize="8xl">🎉</Box>
              <Heading size="2xl" bgGradient="linear(to-r, #667eea, #764ba2)" bgClip="text">
                예측 완료!
              </Heading>
              <Text fontSize="xl" color="gray.600">
                AI 예측 결과가 생성되었습니다
              </Text>
            </VStack>
          </Box>
        </Portal>
      )} */}

      <Box minH="100vh" bg={bgGradient} py={10}>
        <Box
          maxW="6xl"
          mx="auto"
          px={{ base: 4, md: 6 }}
          bg={cardBg}
          borderRadius="2xl"
          shadow={cardShadow}
          border="1px solid"
          borderColor={border}
        >
          <HStack justify="space-between" align="center" py={6}>
            <HStack spacing={3}>
              <Heading size="lg">예측 결과 #{runId}</Heading>
              {run?.status && (
                <Badge colorScheme={run.status === "succeeded" ? "green" : run.status === "failed" ? "red" : "yellow"}>
                  {run.status}
                </Badge>
              )}
            </HStack>
          </HStack>

          <Divider />

          {loading ? (
            <HStack spacing={3} py={12}>
              <Spinner />
              <Text>결과 불러오는 중...</Text>
            </HStack>
          ) : !run?.id ? (
            <VStack align="start" spacing={4} py={10}>
              <Heading size="md">결과를 찾을 수 없습니다.</Heading>
              <Button as={RouterLink} to="/admin/prediction" colorScheme="blue">
                입력 화면으로 돌아가기
              </Button>
            </VStack>
          ) : (
            <>
              <Box px={4} py={3} mb={4} border="1px solid" borderColor={border} borderRadius="xl" bg={cardBg}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <InfoItem label="요청 시각" value={createdAt} />
                  <InfoItem label="완료 시각" value={finishedAt} />
                </SimpleGrid>
              </Box>
              <Box px={4} py={2}>
                <Box textAlign="left" mb={{ base: 2, md: 2 }}>
                  <Divider mb={{ base: 3, md: 4 }} />
                  <Stack
                    direction={{ base: "column", md: "row" }}
                    justify="space-between"
                    align={{ base: "stretch", md: "center" }}
                    spacing={{ base: 2, md: 0 }}
                    w="100%"
                  >
                    <Box>
                      <HStack spacing={3} align="center">
                        <Box
                          w={8}
                          h={8}
                          bg={accent}
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <Text color="white" fontWeight="bold" fontSize="sm">
                            1
                          </Text>
                        </Box>

                        <Heading size={{ base: "md", md: "lg" }} color={textColor}>
                          예측 내용
                        </Heading>

                        <Text
                          fontSize="sm"
                          color="gray.600"
                          ml={2}
                          display={{ base: "none", md: "inline" }}
                          mt={{ base: 2, md: 0 }}
                        >
                          AI 추천값과 정확도 지표를 함께 확인하세요.
                        </Text>
                      </HStack>

                      <Text
                        fontSize="xs"
                        color="gray.500"
                        mt={1}
                        lineHeight="1.4"
                        display={{ base: "block", md: "none" }}
                      >
                        AI 예측 지표와 유사 사례를 확인하세요.
                      </Text>
                    </Box>
                  </Stack>
                </Box>

                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={{ base: 2, md: 4 }} py={4}>
                  <Box
                    border="1px solid"
                    borderColor={border}
                    borderRadius="lg"
                    bg={boardBg}
                    textAlign="center"
                    minH="140px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <Text fontSize={{ base: "xx-small", md: "xs" }} color="black.500" letterSpacing="widest">
                      평당공사비 (원/평)
                    </Text>
                    <Text fontSize={{ base: "lg", md: "2xl" }} fontWeight="700" color="black.900" mt={2}>
                      {areaPyeong > 0 ? <Num value={perPyeong} /> : "-"}
                    </Text>
                  </Box>

                  <Box
                    border="1px solid"
                    borderColor={border}
                    borderRadius="lg"
                    bg={boardBg}
                    textAlign="center"
                    minH="140px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <Text fontSize={{ base: "xx-small", md: "xs" }} color="black.500" letterSpacing="widest">
                      전체공사비 (백만)
                    </Text>
                    <Text fontSize={{ base: "lg", md: "2xl" }} fontWeight="700" color="black.900" mt={2}>
                      <Num value={ui.total_cost} />
                    </Text>
                  </Box>

                  <Box
                    border="1px solid"
                    borderColor={border}
                    borderRadius="lg"
                    bg={boardBg}
                    minH="140px"
                    display={{ base: "none", md: "flex" }}
                    alignItems="center"
                    justifyContent="center"
                    colSpan={{ base: 1, md: 1 }}
                    justifySelf="center"
                    mx="auto"
                    w={{ base: "220px", md: "auto" }}
                  >
                    <AccuracyRingTicker
                      label={totalCostAccuracyLabel}
                      value={totalCostAccuracyItem?.value}
                      ringColor={
                        ringColorToken[accuracyColor(totalCostAccuracyItem?.value)] || "gray.400"
                      }
                    />
                  </Box>
                </SimpleGrid>

                <Box
                  border="1px solid"
                  borderColor={border}
                  borderRadius="lg"
                  bg={boardBg}
                  minH="92px"
                  display={{ base: "flex", md: "none" }}
                  alignItems="center"
                  px={4}
                  py={3}
                  mt={2}
                  gap={3}
                  flexWrap="nowrap"
                  w="100%"
                >
                  <Text fontSize="sm" fontWeight="600" color={textColor} whiteSpace="nowrap">
                    {totalCostAccuracyLabel} 정확도
                  </Text>
                  <Progress
                    value={totalCostAccuracyPct ?? 0}
                    colorScheme={mobileProgressColorScheme}
                    size="sm"
                    borderRadius="lg"
                    max={100}
                    flex="1"
                  />
                  <Text fontSize="xl" fontWeight="700" color={textColor} minW="64px" textAlign="right">
                    {totalCostAccuracyPct != null ? `${totalCostAccuracyPct}%` : "-"}
                  </Text>
                </Box>

                <Box mt={4} p={4} border="1px solid" borderColor={border} borderRadius="xl" bg={cardBg}>
                  <HStack spacing={3} align="center">
                    <Box
                      w={8}
                      h={8}
                      bg={accent}
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text color="white" fontWeight="bold" fontSize="sm">
                        참고
                      </Text>
                    </Box>

                    <Heading size={{ base: "md", md: "sm" }} color={textColor}>
                      비슷한 과거 사례 분석
                    </Heading>

                    <Text
                      fontSize="sm"
                      color="gray.600"
                      ml={2}
                      display={{ base: "none", md: "block" }}
                      mt={{ base: 2, md: 0 }}
                    >
                      유사 사례 비용 비교 결과입니다. <b>아래 항목을 확인하세요. 🤨</b>
                    </Text>
                  </HStack>

                  <Text
                    fontSize="xs"
                    color="gray.600"
                    mt={1}
                    lineHeight="1.4"
                    display={{ base: "block", md: "none" }}
                  >
                    <b>아래 항목을 확인하세요. 🤨</b>
                  </Text>
                  <Box mt={4}>
                    <SimilarNotesAlert
                      heading={`${payloadInput?.유형 || "유형 정보"} 기준 참고`}
                      description={similarDescription}
                    />
                  </Box>
                  {renderSimilarNotesBox({
                    heading: `${payloadInput?.유형 || "유형 정보"} 기준 참고`,
                    description: similarDescription,
                    onOpenCases: openCasesModal,
                  })}
                </Box>
              </Box>

              <Box px={4} py={2}>
                <Box textAlign="left" mb={{ base: 2, md: 2 }}>
                  <Divider mb={{ base: 3, md: 4 }} />
                  <Stack
                    direction={{ base: "column", md: "row" }}
                    justify="space-between"
                    align={{ base: "stretch", md: "center" }}
                    spacing={{ base: 2, md: 0 }}
                    w="100%"
                  >
                    <Box>
                      <HStack spacing={3} align="center">
                        <Box
                          w={8}
                          h={8}
                          bg={accent}
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <Text color="white" fontWeight="bold" fontSize="sm">
                            2
                          </Text>
                        </Box>

                        <Heading size={{ base: "md", md: "lg" }} color={textColor}>
                          참고 비용
                        </Heading>

                        <Text
                          fontSize="sm"
                          color="gray.600"
                          ml={2}
                          display={{ base: "none", md: "inline" }}
                          mt={{ base: 2, md: 0 }}
                        >
                          과거 사례 데이터를 참고해 보정된 비용입니다. <b>아래 항목을 확인하세요. 🤨</b>
                        </Text>
                      </HStack>

                      <Text
                        fontSize="xs"
                        color="gray.600"
                        mt={1}
                        lineHeight="1.4"
                        display={{ base: "block", md: "none" }}
                      >
                        <b>과거 사례를 기반으로 보정한 비용을 확인하세요. 🤨</b>
                      </Text>
                    </Box>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowCostDetails((v) => !v)}
                      rightIcon={
                        <ChevronDownIcon
                          transform={showCostDetails ? "rotate(180deg)" : "rotate(0deg)"}
                          transition="transform 0.2s"
                        />
                      }
                      alignSelf={{ base: "flex-start", md: "center" }}
                      w={{ base: "100%", md: "auto" }}
                    >
                      {showCostDetails ? "닫기" : "자세히보기"}
                    </Button>
                  </Stack>
                </Box>

                <Collapse in={showCostDetails} animateOpacity>
                  <Alert status="info" borderRadius="md" alignItems="flex-start" mb={4}>
                    <AlertIcon mt={0.5} />
                    <VStack align="flex-start" spacing={1} fontSize="sm">
                      <Text fontWeight="800" color="black.600">
                        Q. 참고 비용 항목은 정확한가요?
                      </Text>
                      <Text>
                        A. 아래에 표시된 금액은{" "}
                        AI가 과거 공사 데이터를 학습해 예측한 결과를 평당 공사비 기준으로 보정한 추정치
                        입니다. 실제 시공 비용과는 차이가 있을 수 있으니, <b>참고용 자료로만 활용해 주세요.</b>
                      </Text>
                    </VStack>
                  </Alert>
                  {/* <Box p={3} bg="gray.50" border="1px" borderColor="gray.200" rounded="md" mb={4}>
                    <Text fontSize="sm" color="gray.700" textAlign="center">
                      
                    </Text>
                  </Box> */}
                  {canViewAccuracy && otherAccuracyItems.length > 0 && (
                    <Box mt={4} p={4} border="1px solid" borderColor={border} borderRadius="xl" bg={cardBg}>
                      <Heading size="sm" mb={3}>
                        정확도
                      </Heading>
                      <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 4 }} spacing={3}>
                        {otherAccuracyItems.map(({ key, label, value }) => {
                          const scheme = accuracyColor(value);
                          const color = ringColorToken[scheme] || "gray.400";
                          const pct = Math.max(0, Math.min(100, value));
                          return (
                            <VStack key={key} p={3} bg={chipBg} border="1px solid" borderColor={border} borderRadius="lg">
                              <CircularProgress value={pct} size="70px" thickness="10px" color={color}>
                                <CircularProgressLabel fontSize="sm">{pct.toFixed(0)}%</CircularProgressLabel>
                              </CircularProgress>
                              <Text fontSize="sm" fontWeight="600">
                                {label}
                              </Text>
                            </VStack>
                          );
                        })}
                      </SimpleGrid>
                    </Box>
                  )}
                  <Box p={4} border="1px solid" borderColor={border} borderRadius="xl" bg={cardBg}>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6}>
                      <Stat>
                        <StatLabel>건축비 (원/평)</StatLabel>
                        <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                          <Num value={ui.building_cost} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>토목 (원/평)</StatLabel>
                        <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                          <Num value={ui.civil_cost} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>전기 (원/평)</StatLabel>
                        <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                          <Num value={ui.electrical_cost} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>설비 (원/평)</StatLabel>
                        <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                          <Num value={ui.facility_cost} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>경상비 (원/평)</StatLabel>
                        <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                          <Num value={ui.opex_cost} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>기타 (원/평)</StatLabel>
                        <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                          <Num value={ui.etc_cost} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>공통 (원/평)</StatLabel>
                        <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                          <Num value={ui.common_cost} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel fontWeight="bold" color="blue.600">
                          총 합계 (원/평)
                        </StatLabel>
                        <StatNumber fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="blue.700">
                          <Num
                            value={
                              (ui.building_cost || 0) +
                              (ui.civil_cost || 0) +
                              (ui.electrical_cost || 0) +
                              (ui.facility_cost || 0) +
                              (ui.opex_cost || 0) +
                              (ui.etc_cost || 0) +
                              (ui.common_cost || 0)
                            }
                          />
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>
                </Collapse>
              </Box>

              <Box px={4} pb={8}>                  
                <Divider mb={{ base: 6, md: 4 }} />
                <Stack
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                  spacing={{ base: 2, md: 0 }}
                  w="100%"
                  mb={4}
                >
                  <Box>
                    <HStack spacing={3} align="center">
                      <Box
                        w={8}
                        h={8}
                        bg={accent}
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Text color="white" fontWeight="bold" fontSize="sm">
                          3
                        </Text>
                      </Box>

                      <Heading size={{ base: "md", md: "lg" }} color={textColor}>
                        입력 요약
                      </Heading>

                      <Text
                        fontSize="sm"
                        color="gray.600"
                        ml={2}
                        display={{ base: "none", md: "inline" }}
                        mt={{ base: 2, md: 0 }}
                      >
                        제출한 값을 다시 한번 확인하세요.
                      </Text>
                    </HStack>

                    <Text
                      fontSize="xs"
                      color="gray.500"
                      mt={1}
                      lineHeight="1.4"
                      display={{ base: "block", md: "none" }}
                    >
                      제출한 값을 다시 한번 확인하세요.
                    </Text>
                  </Box>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowInputSummary((prev) => !prev)}
                    rightIcon={
                      <ChevronDownIcon
                        transform={showInputSummary ? "rotate(180deg)" : "rotate(0deg)"}
                        transition="transform 0.2s"
                      />
                    }
                    alignSelf={{ base: "flex-start", md: "center" }}
                    w={{ base: "100%", md: "auto" }}
                  >
                    {showInputSummary ? "닫기" : "자세히보기"}
                  </Button>
                </Stack>

                <Collapse in={showInputSummary} animateOpacity>
                  <VStack spacing={6} align="stretch">
                    <Box>
                      <HStack justify="space-between" align="center" mb={3}>
                        <Badge colorScheme="purple">기본 정보</Badge>
                      </HStack>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        {basicInfoItems.map((item) => (
                          <InfoItem key={item.label} label={item.label} value={item.value} />
                        ))}
                      </SimpleGrid>
                    </Box>

                    <Box>
                      <HStack justify="space-between" align="center" mb={3}>
                        <Badge colorScheme="blue">공사 개요</Badge>
                      </HStack>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        {constructionOverviewItems.map((item) => (
                          <InfoItem key={item.label} label={item.label} value={item.value} />
                        ))}
                      </SimpleGrid>
                    </Box>

                    <Box>
                      <HStack justify="space-between" align="center" mb={3}>
                        <Badge colorScheme="teal">선택 사항</Badge>
                      </HStack>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        {selectionItems.map((item) => (
                          <InfoItem key={item.label} label={item.label} value={item.value} />
                        ))}
                      </SimpleGrid>
                      <Box mt={4}>
                        <Text fontSize="sm" color="gray.500" mb={2}>
                          비용 입력
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                          {costFields.map(({ label, key }) => (
                            <InfoItem
                              key={key}
                              label={label}
                              value={formatInputNumber(payloadInput?.[key], { maximumFractionDigits: 0 })}
                            />
                          ))}
                          <InfoItem label="평당 공사비 (원/평)" value={formattedPerPyeongInput} />
                        </SimpleGrid>
                      </Box>
                    </Box>
                  </VStack>
                </Collapse>
                <Divider mt={4} borderColor={border} />

                <HStack mt={6} spacing={3} justify="flex-end" w="full">
                  <Button as={RouterLink} to="/admin/prediction" colorScheme="blue" rightIcon={<ChevronRightIcon />}>
                    다시 예측하기
                  </Button>
                </HStack>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Modal isOpen={casesDisclosure.isOpen} onClose={casesDisclosure.onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            유사 사례 목록{" "}
            <Text as="span" fontWeight="600" color="blue.600">
              ({similarAverage?.count?.toLocaleString() || 0}건)
            </Text>
            {similarAverage?.warning && (
              <Badge ml={2} colorScheme="orange">
                사례 부족
              </Badge>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.500" mb={3}>
              최종 적용된 기준:{" "}
              {areaRangeLabel ? `연면적 ${areaRangeLabel}` : "연면적 미확인"} /{" "}
              {avgRangeLabel ? `평균평형 ${avgRangeLabel}` : "평균평형 미확인"} /{" "}
              {householdsRangeLabel ? `세대수 ${householdsRangeLabel}` : "세대수 미확인"}
            </Text>
            <Divider mb={4} />
            {casesLoading ? (
              <Flex justify="center" py={8}>
                <Spinner />
              </Flex>
            ) : casesError ? (
              <Text color="red.500" fontSize="sm">
                {casesError}
              </Text>
            ) : similarCases.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                서버에서 유사 사례 데이터를 가져올 수 없습니다.
              </Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {similarCases.map((record) => (
                  <Box key={record.id} p={3} borderWidth="1px" borderRadius="md" borderColor={border} bg={boardBg}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontWeight="600">{record.project_name || "프로젝트 정보 없음"}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {record.region || "-"}
                      </Text>
                    </HStack>
                    <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={3}>
                      <Stat>
                        <StatLabel fontSize="xs">연면적 (평)</StatLabel>
                        <StatNumber fontSize="sm">
                          <Num value={record.total_area_py} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel fontSize="xs">평균평형 (평)</StatLabel>
                        <StatNumber fontSize="sm">
                          <Num value={record.avg_area_py} />
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel fontSize="xs">세대수</StatLabel>
                        <StatNumber fontSize="sm">
                          <Num value={record.households} />
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      공법: {record.construction_method || "미등록"}, 경사여부: {record.slope_type || "미등록"}
                    </Text>
                    <Text fontSize="sm" color="gray.700" mt={2}>
                      총공사비 (백만):{" "}
                      <Text as="span" fontWeight="600">
                        {record.total_cost != null ? `${formatNumber(record.total_cost)}백만` : "정보 없음"}
                      </Text>
                    </Text>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={casesDisclosure.onClose}>닫기</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
